import {rpAppLink} from "@pg-mono/rp-routes";
import {PRICES_CITIES_SLUGS} from "@pg-rp/navigation";

import type {ISearchInputRegion} from "../../../../search/components/ISearchInputValue";
import {saveRecentRegionSearch} from "../../../../search/utils/recent_filters_storage";
import {
    PRICES_RECENT_SEARCHES_STORAGE_KEY,
    PRICES_RECENT_SEARCHES_STORE_VERSION,
    PRICES_SEARCH_MIRROR_MAX_AGE_MS,
    PRICES_SEARCH_MIRROR_STORAGE_KEY,
    PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX,
    PRICES_SEARCH_RECENT_SHOWN,
    PRICES_SEARCH_RECENT_STORED
} from "../../constants/prices_search";
import type {
    IPricesRecentHidden,
    IPricesRecentSearch,
    IPricesRecentStore,
    IPricesSearchMirrorMarker,
    PricesRecentSearchKind
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../../types/IRcnAddress";
import {isValidSearchInputRegion} from "./is_valid_search_input_region";
import {createLocalStorageListStore, parseStoredList} from "./local_storage_list_store";

/*
 * Recent searches of the prices search: its own store in localStorage, merged on read with the main search
 * store (`mapMainRecentToPrices`). Cities and districts are also mirrored back to the main store - districts right away,
 * cities through a one-shot sessionStorage marker consumed by the city page.
 */

export const EMPTY_PRICES_RECENT_STORE: IPricesRecentStore = {v: PRICES_RECENT_SEARCHES_STORE_VERSION, items: [], hidden: [], clearedAt: 0};

const RECENT_KINDS: readonly PricesRecentSearchKind[] = ["city", "district", "address"];

export const getPricesRecentCityId = (citySlug: string): string => `city:${citySlug}`;

export const getPricesRecentDistrictId = (regionId: number): string => `district:${regionId}`;

/** Built from the raw RCN values, the same `city|street|number` key the building-number prices use. */
export const getPricesRecentAddressId = ({city, street, number}: IRcnAddressKey): string => `address:${city}|${street}|${number}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const parseRcnAddressKey = (value: unknown): IRcnAddressKey | null =>
    isRecord(value) && isNonEmptyString(value.city) && isNonEmptyString(value.street) && isNonEmptyString(value.number)
        ? {city: value.city, street: value.street, number: value.number}
        : null;

const isRecentKind = (value: unknown): value is PricesRecentSearchKind => RECENT_KINDS.includes(value as PricesRecentSearchKind);

const isRegionId = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0;

/** The id an entry of this kind must carry, so the same place gets the same id in both stores (dedupe, hiding). */
const getCanonicalId = (kind: PricesRecentSearchKind, citySlug: string, regionId: number | undefined, rcn: IRcnAddressKey | undefined): string | null => {
    switch (kind) {
        case "city":
            return getPricesRecentCityId(citySlug);
        case "district":
            return regionId === undefined ? null : getPricesRecentDistrictId(regionId);
        case "address":
            return rcn ? getPricesRecentAddressId(rcn) : null;
    }
};

/** One stored entry, or null when any field is off - the entry is then dropped, the rest of the store survives. */
export const parsePricesRecentSearch = (value: unknown): IPricesRecentSearch | null => {
    if (!isRecord(value)) {
        return null;
    }

    const {id, kind, title, subtitle, href, timestamp, citySlug} = value;
    const regionId = isRegionId(value.regionId) ? value.regionId : undefined;
    const rcn = value.rcn === undefined ? undefined : parseRcnAddressKey(value.rcn);

    if (
        !isNonEmptyString(id) ||
        !isRecentKind(kind) ||
        !isNonEmptyString(title) ||
        typeof subtitle !== "string" ||
        !isNonEmptyString(href) ||
        !href.startsWith(rpAppLink.prices.flats.base()) ||
        typeof timestamp !== "number" ||
        !Number.isFinite(timestamp) ||
        !isNonEmptyString(citySlug) ||
        !PRICES_CITIES_SLUGS.includes(citySlug) ||
        (value.regionId !== undefined && regionId === undefined) ||
        rcn === null ||
        id !== getCanonicalId(kind, citySlug, regionId, rcn)
    ) {
        return null;
    }

    return {
        id,
        kind,
        title,
        subtitle,
        href,
        timestamp,
        citySlug,
        ...(regionId !== undefined ? {regionId} : {}),
        ...(rcn ? {rcn} : {})
    };
};

const parseHidden = (value: unknown): IPricesRecentHidden | null =>
    isRecord(value) && isNonEmptyString(value.id) && typeof value.hiddenAt === "number" && Number.isFinite(value.hiddenAt)
        ? {id: value.id, hiddenAt: value.hiddenAt}
        : null;

/** Unique ids, newest first, at most `max`; on a duplicate id the newer timestamp wins. */
const dedupeNewestFirst = (items: readonly IPricesRecentSearch[], max: number): IPricesRecentSearch[] => {
    const byId = new Map<string, IPricesRecentSearch>();

    for (const item of items) {
        const existing = byId.get(item.id);

        if (!existing || item.timestamp > existing.timestamp) {
            byId.set(item.id, item);
        }
    }

    return [...byId.values()].sort((a, b) => b.timestamp - a.timestamp).slice(0, max);
};

export const parsePricesRecentStore = (value: unknown): IPricesRecentStore | null => {
    if (!isRecord(value) || value.v !== PRICES_RECENT_SEARCHES_STORE_VERSION) {
        return null;
    }

    const items = parseStoredList(value.items, parsePricesRecentSearch, Number.MAX_SAFE_INTEGER);
    const hidden = parseStoredList(value.hidden, parseHidden, PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX);
    const clearedAt = typeof value.clearedAt === "number" && Number.isFinite(value.clearedAt) && value.clearedAt > 0 ? value.clearedAt : 0;

    return {v: PRICES_RECENT_SEARCHES_STORE_VERSION, items: dedupeNewestFirst(items, PRICES_SEARCH_RECENT_STORED), hidden, clearedAt};
};

export const pricesRecentSearchesStore = createLocalStorageListStore<IPricesRecentStore>({
    key: PRICES_RECENT_SEARCHES_STORAGE_KEY,
    parse: parsePricesRecentStore,
    empty: EMPTY_PRICES_RECENT_STORE
});

// ---------------------------------------------------------------------------------------------------------------
// Pure store transitions
// ---------------------------------------------------------------------------------------------------------------

/** Adds or refreshes an entry and un-hides its id (picking a location again brings it back). */
export const addToPricesRecentStore = (store: IPricesRecentStore, entry: IPricesRecentSearch): IPricesRecentStore => ({
    ...store,
    items: dedupeNewestFirst([entry, ...store.items], PRICES_SEARCH_RECENT_STORED),
    hidden: store.hidden.filter((hiddenEntry) => hiddenEntry.id !== entry.id)
});

/** "×": drops the own entry and masks main-search entries for the id saved up to `now` - a later search shows again. */
export const removeFromPricesRecentStore = (store: IPricesRecentStore, id: string, now: number): IPricesRecentStore => ({
    ...store,
    items: store.items.filter((item) => item.id !== id),
    hidden: [{id, hiddenAt: now}, ...store.hidden.filter((hiddenEntry) => hiddenEntry.id !== id)].slice(0, PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX)
});

/** "Wyczyść": main-search entries up to `now` stop showing; the main store itself is not touched. */
export const clearPricesRecentStore = (now: number): IPricesRecentStore => ({...EMPTY_PRICES_RECENT_STORE, clearedAt: now});

/** Own entries plus main-search entries saved after their "×" and after the last "Wyczyść", unique by id, newest first. */
export const mergePricesRecentSearches = (
    store: IPricesRecentStore,
    mainEntries: readonly IPricesRecentSearch[],
    limit: number = PRICES_SEARCH_RECENT_SHOWN
): IPricesRecentSearch[] => {
    const hiddenAt = new Map(store.hidden.map((hiddenEntry) => [hiddenEntry.id, hiddenEntry.hiddenAt]));
    const visibleMainEntries = mainEntries.filter((entry) => entry.timestamp > store.clearedAt && entry.timestamp > (hiddenAt.get(entry.id) ?? 0));

    return dedupeNewestFirst([...store.items, ...visibleMainEntries], limit);
};

// ---------------------------------------------------------------------------------------------------------------
// Store actions (no-ops when localStorage is unavailable)
// ---------------------------------------------------------------------------------------------------------------

type PricesRecentSearchInput = Omit<IPricesRecentSearch, "timestamp"> & {timestamp?: number};

/** Saves a picked location (city, district, address, number, popular, chip, nearby, a recent entry again). */
export const addPricesRecentSearch = (entry: PricesRecentSearchInput): boolean => {
    const parsed = parsePricesRecentSearch({...entry, timestamp: entry.timestamp ?? Date.now()});

    return parsed ? pricesRecentSearchesStore.update((store) => addToPricesRecentStore(store, parsed)) : false;
};

export const removePricesRecentSearch = (id: string): boolean =>
    pricesRecentSearchesStore.update((store) => removeFromPricesRecentStore(store, id, Date.now()));

export const clearPricesRecentSearches = (): boolean => pricesRecentSearchesStore.write(clearPricesRecentStore(Date.now()));

/** Rewrites the subtitle of an own entry in place (no reorder); used by the address page once the district is known. */
export const updatePricesRecentSearchSubtitle = (id: string, subtitle: string): boolean =>
    pricesRecentSearchesStore.update((store) =>
        store.items.some((item) => item.id === id && item.subtitle !== subtitle)
            ? {...store, items: store.items.map((item) => (item.id === id ? {...item, subtitle} : item))}
            : store
    );

// ---------------------------------------------------------------------------------------------------------------
// Mirroring to the main search store
// ---------------------------------------------------------------------------------------------------------------

/** A district is picked together with its full region object, so it goes to the main store before navigating. */
export const savePricesDistrictToMainSearch = (region: ISearchInputRegion): boolean => isValidSearchInputRegion(region) && saveRecentRegionSearch(region);

const getSessionStorage = (): Storage | null => (typeof window === "undefined" ? null : window.sessionStorage);

/** A city has no region object at hand; the landing page fetches it (`useMirrorPricesRecentToMainSearch`). */
export const writePricesSearchMirrorMarker = (regionId: number): boolean => {
    try {
        const storage = getSessionStorage();

        if (!storage) {
            return false;
        }

        const marker: IPricesSearchMirrorMarker = {regionId, ts: Date.now()};
        storage.setItem(PRICES_SEARCH_MIRROR_STORAGE_KEY, JSON.stringify(marker));

        return true;
    } catch {
        return false;
    }
};

/** Reads and always removes the marker; returns it only when it is for `regionId` and younger than the max age. */
export const consumePricesSearchMirrorMarker = (regionId: number, now: number = Date.now()): IPricesSearchMirrorMarker | null => {
    try {
        const storage = getSessionStorage();
        const raw = storage?.getItem(PRICES_SEARCH_MIRROR_STORAGE_KEY);

        if (!storage || raw == null) {
            return null;
        }

        storage.removeItem(PRICES_SEARCH_MIRROR_STORAGE_KEY);

        const marker: unknown = JSON.parse(raw);

        if (!isRecord(marker) || marker.regionId !== regionId || typeof marker.ts !== "number") {
            return null;
        }

        const age = now - marker.ts;

        return age >= 0 && age < PRICES_SEARCH_MIRROR_MAX_AGE_MS ? {regionId, ts: marker.ts} : null;
    } catch {
        return null;
    }
};
import {rpAppLink} from "@pg-mono/rp-routes";
import {PRICES_CITIES_SLUGS} from "@pg-rp/navigation";

import type {ISearchInputRegion} from "../../../../search/components/ISearchInputValue";
import {saveRecentRegionSearch} from "../../../../search/utils/recent_filters_storage";
import {
    PRICES_RECENT_SEARCHES_STORAGE_KEY,
    PRICES_RECENT_SEARCHES_STORE_VERSION,
    PRICES_SEARCH_MIRROR_MAX_AGE_MS,
    PRICES_SEARCH_MIRROR_STORAGE_KEY,
    PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX,
    PRICES_SEARCH_RECENT_SHOWN,
    PRICES_SEARCH_RECENT_STORED
} from "../../constants/prices_search";
import type {
    IPricesRecentHidden,
    IPricesRecentSearch,
    IPricesRecentStore,
    IPricesSearchMirrorMarker,
    PricesRecentSearchKind
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../../types/IRcnAddress";
import {isValidSearchInputRegion} from "./is_valid_search_input_region";
import {createLocalStorageListStore, parseStoredList} from "./local_storage_list_store";

/*
 * Recent searches of the prices search: its own store in localStorage, merged on read with the main search
 * store (`mapMainRecentToPrices`). Cities and districts are also mirrored back to the main store - districts right away,
 * cities through a one-shot sessionStorage marker consumed by the city page.
 */

export const EMPTY_PRICES_RECENT_STORE: IPricesRecentStore = {v: PRICES_RECENT_SEARCHES_STORE_VERSION, items: [], hidden: [], clearedAt: 0};

const RECENT_KINDS: readonly PricesRecentSearchKind[] = ["city", "district", "address"];

export const getPricesRecentCityId = (citySlug: string): string => `city:${citySlug}`;

export const getPricesRecentDistrictId = (regionId: number): string => `district:${regionId}`;

/** Built from the raw RCN values, the same `city|street|number` key the building-number prices use. */
export const getPricesRecentAddressId = ({city, street, number}: IRcnAddressKey): string => `address:${city}|${street}|${number}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const parseRcnAddressKey = (value: unknown): IRcnAddressKey | null =>
    isRecord(value) && isNonEmptyString(value.city) && isNonEmptyString(value.street) && isNonEmptyString(value.number)
        ? {city: value.city, street: value.street, number: value.number}
        : null;

const isRecentKind = (value: unknown): value is PricesRecentSearchKind => RECENT_KINDS.includes(value as PricesRecentSearchKind);

const isRegionId = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0;

/** The id an entry of this kind must carry, so the same place gets the same id in both stores (dedupe, hiding). */
const getCanonicalId = (kind: PricesRecentSearchKind, citySlug: string, regionId: number | undefined, rcn: IRcnAddressKey | undefined): string | null => {
    switch (kind) {
        case "city":
            return getPricesRecentCityId(citySlug);
        case "district":
            return regionId === undefined ? null : getPricesRecentDistrictId(regionId);
        case "address":
            return rcn ? getPricesRecentAddressId(rcn) : null;
    }
};

/** One stored entry, or null when any field is off - the entry is then dropped, the rest of the store survives. */
export const parsePricesRecentSearch = (value: unknown): IPricesRecentSearch | null => {
    if (!isRecord(value)) {
        return null;
    }

    const {id, kind, title, subtitle, href, timestamp, citySlug} = value;
    const regionId = isRegionId(value.regionId) ? value.regionId : undefined;
    const rcn = value.rcn === undefined ? undefined : parseRcnAddressKey(value.rcn);

    if (
        !isNonEmptyString(id) ||
        !isRecentKind(kind) ||
        !isNonEmptyString(title) ||
        typeof subtitle !== "string" ||
        !isNonEmptyString(href) ||
        !href.startsWith(rpAppLink.prices.flats.base()) ||
        typeof timestamp !== "number" ||
        !Number.isFinite(timestamp) ||
        !isNonEmptyString(citySlug) ||
        !PRICES_CITIES_SLUGS.includes(citySlug) ||
        (value.regionId !== undefined && regionId === undefined) ||
        rcn === null ||
        id !== getCanonicalId(kind, citySlug, regionId, rcn)
    ) {
        return null;
    }

    return {
        id,
        kind,
        title,
        subtitle,
        href,
        timestamp,
        citySlug,
        ...(regionId !== undefined ? {regionId} : {}),
        ...(rcn ? {rcn} : {})
    };
};

const parseHidden = (value: unknown): IPricesRecentHidden | null =>
    isRecord(value) && isNonEmptyString(value.id) && typeof value.hiddenAt === "number" && Number.isFinite(value.hiddenAt)
        ? {id: value.id, hiddenAt: value.hiddenAt}
        : null;

/** Unique ids, newest first, at most `max`; on a duplicate id the newer timestamp wins. */
const dedupeNewestFirst = (items: readonly IPricesRecentSearch[], max: number): IPricesRecentSearch[] => {
    const byId = new Map<string, IPricesRecentSearch>();

    for (const item of items) {
        const existing = byId.get(item.id);

        if (!existing || item.timestamp > existing.timestamp) {
            byId.set(item.id, item);
        }
    }

    return [...byId.values()].sort((a, b) => b.timestamp - a.timestamp).slice(0, max);
};

export const parsePricesRecentStore = (value: unknown): IPricesRecentStore | null => {
    if (!isRecord(value) || value.v !== PRICES_RECENT_SEARCHES_STORE_VERSION) {
        return null;
    }

    const items = parseStoredList(value.items, parsePricesRecentSearch, Number.MAX_SAFE_INTEGER);
    const hidden = parseStoredList(value.hidden, parseHidden, PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX);
    const clearedAt = typeof value.clearedAt === "number" && Number.isFinite(value.clearedAt) && value.clearedAt > 0 ? value.clearedAt : 0;

    return {v: PRICES_RECENT_SEARCHES_STORE_VERSION, items: dedupeNewestFirst(items, PRICES_SEARCH_RECENT_STORED), hidden, clearedAt};
};

export const pricesRecentSearchesStore = createLocalStorageListStore<IPricesRecentStore>({
    key: PRICES_RECENT_SEARCHES_STORAGE_KEY,
    parse: parsePricesRecentStore,
    empty: EMPTY_PRICES_RECENT_STORE
});

// ---------------------------------------------------------------------------------------------------------------
// Pure store transitions
// ---------------------------------------------------------------------------------------------------------------

/** Adds or refreshes an entry and un-hides its id (picking a location again brings it back). */
export const addToPricesRecentStore = (store: IPricesRecentStore, entry: IPricesRecentSearch): IPricesRecentStore => ({
    ...store,
    items: dedupeNewestFirst([entry, ...store.items], PRICES_SEARCH_RECENT_STORED),
    hidden: store.hidden.filter((hiddenEntry) => hiddenEntry.id !== entry.id)
});

/** "×": drops the own entry and masks main-search entries for the id saved up to `now` - a later search shows again. */
export const removeFromPricesRecentStore = (store: IPricesRecentStore, id: string, now: number): IPricesRecentStore => ({
    ...store,
    items: store.items.filter((item) => item.id !== id),
    hidden: [{id, hiddenAt: now}, ...store.hidden.filter((hiddenEntry) => hiddenEntry.id !== id)].slice(0, PRICES_SEARCH_RECENT_HIDDEN_IDS_MAX)
});

/** "Wyczyść": main-search entries up to `now` stop showing; the main store itself is not touched. */
export const clearPricesRecentStore = (now: number): IPricesRecentStore => ({...EMPTY_PRICES_RECENT_STORE, clearedAt: now});

/** Own entries plus main-search entries saved after their "×" and after the last "Wyczyść", unique by id, newest first. */
export const mergePricesRecentSearches = (
    store: IPricesRecentStore,
    mainEntries: readonly IPricesRecentSearch[],
    limit: number = PRICES_SEARCH_RECENT_SHOWN
): IPricesRecentSearch[] => {
    const hiddenAt = new Map(store.hidden.map((hiddenEntry) => [hiddenEntry.id, hiddenEntry.hiddenAt]));
    const visibleMainEntries = mainEntries.filter((entry) => entry.timestamp > store.clearedAt && entry.timestamp > (hiddenAt.get(entry.id) ?? 0));

    return dedupeNewestFirst([...store.items, ...visibleMainEntries], limit);
};

// ---------------------------------------------------------------------------------------------------------------
// Store actions (no-ops when localStorage is unavailable)
// ---------------------------------------------------------------------------------------------------------------

type PricesRecentSearchInput = Omit<IPricesRecentSearch, "timestamp"> & {timestamp?: number};

/** Saves a picked location (city, district, address, number, popular, chip, nearby, a recent entry again). */
export const addPricesRecentSearch = (entry: PricesRecentSearchInput): boolean => {
    const parsed = parsePricesRecentSearch({...entry, timestamp: entry.timestamp ?? Date.now()});

    return parsed ? pricesRecentSearchesStore.update((store) => addToPricesRecentStore(store, parsed)) : false;
};

export const removePricesRecentSearch = (id: string): boolean =>
    pricesRecentSearchesStore.update((store) => removeFromPricesRecentStore(store, id, Date.now()));

export const clearPricesRecentSearches = (): boolean => pricesRecentSearchesStore.write(clearPricesRecentStore(Date.now()));

/** Rewrites the subtitle of an own entry in place (no reorder); used by the address page once the district is known. */
export const updatePricesRecentSearchSubtitle = (id: string, subtitle: string): boolean =>
    pricesRecentSearchesStore.update((store) =>
        store.items.some((item) => item.id === id && item.subtitle !== subtitle)
            ? {...store, items: store.items.map((item) => (item.id === id ? {...item, subtitle} : item))}
            : store
    );

// ---------------------------------------------------------------------------------------------------------------
// Mirroring to the main search store
// ---------------------------------------------------------------------------------------------------------------

/** A district is picked together with its full region object, so it goes to the main store before navigating. */
export const savePricesDistrictToMainSearch = (region: ISearchInputRegion): boolean => isValidSearchInputRegion(region) && saveRecentRegionSearch(region);

const getSessionStorage = (): Storage | null => (typeof window === "undefined" ? null : window.sessionStorage);

/** A city has no region object at hand; the landing page fetches it (`useMirrorPricesRecentToMainSearch`). */
export const writePricesSearchMirrorMarker = (regionId: number): boolean => {
    try {
        const storage = getSessionStorage();

        if (!storage) {
            return false;
        }

        const marker: IPricesSearchMirrorMarker = {regionId, ts: Date.now()};
        storage.setItem(PRICES_SEARCH_MIRROR_STORAGE_KEY, JSON.stringify(marker));

        return true;
    } catch {
        return false;
    }
};

/** Reads and always removes the marker; returns it only when it is for `regionId` and younger than the max age. */
export const consumePricesSearchMirrorMarker = (regionId: number, now: number = Date.now()): IPricesSearchMirrorMarker | null => {
    try {
        const storage = getSessionStorage();
        const raw = storage?.getItem(PRICES_SEARCH_MIRROR_STORAGE_KEY);

        if (!storage || raw == null) {
            return null;
        }

        storage.removeItem(PRICES_SEARCH_MIRROR_STORAGE_KEY);

        const marker: unknown = JSON.parse(raw);

        if (!isRecord(marker) || marker.regionId !== regionId || typeof marker.ts !== "number") {
            return null;
        }

        const age = now - marker.ts;

        return age >= 0 && age < PRICES_SEARCH_MIRROR_MAX_AGE_MS ? {regionId, ts: marker.ts} : null;
    } catch {
        return null;
    }
};
